/**
 * Home. Mantém a estrutura do protótipo (hero → diferenciais → novas → usadas
 * → faixa de troca → rodapé) e as âncoras `#novas`/`#usadas`.
 *
 * DIFERENÇA: as seções mostram uma PRÉVIA curada, não o catálogo inteiro.
 * Com 40+ bikes, listar tudo aqui significaria mandar todos os produtos e
 * todas as imagens para todo visitante, num scroll infinito no celular. Cada
 * seção termina em "Ver todas (N)" apontando para `/bicicletas`.
 */
import { useMemo } from 'react'
import { formatarPreco, horarioParaSchema } from '@peniel/shared'

import { Meta } from '@/shared/seo/Meta.jsx'
import { JsonLd } from '@/shared/seo/JsonLd.jsx'
import { useConfigLoja } from '@/shared/hooks/useConfigLoja.js'
import { Hero } from '../componentes/Hero.jsx'
import { TiraDeDiferenciais } from '../componentes/TiraDeDiferenciais.jsx'
import { SecaoProdutos } from '../componentes/SecaoProdutos.jsx'
import { BannerTroca } from '../componentes/BannerTroca.jsx'
import { useProdutosVitrine } from '@/shared/hooks/useProdutosVitrine.js'

const QUANTIDADE_NOVAS = 6
const QUANTIDADE_USADAS = 8

export default function PaginaInicial() {
  const novas = useProdutosVitrine('NOVA', QUANTIDADE_NOVAS)
  const usadas = useProdutosVitrine('USADA', QUANTIDADE_USADAS)
  const config = useConfigLoja()

  /**
   * O horário é TEXTO LIVRE no painel, porque é o que o cliente lê no rodapé.
   * `horarioParaSchema` traduz para o formato que o Google entende e devolve
   * vazio quando não consegue — dado estruturado errado é pior que ausente.
   */
  const horarioSchema = useMemo(() => horarioParaSchema(config.lojaHorario), [config.lojaHorario])

  /**
   * Faixa de preço a partir das bicicletas já carregadas na home.
   *
   * Vem das prévias, e não de uma consulta nova: a home já tem esses dados na
   * mão, e um request a mais no caminho do LCP custaria mais do que a precisão
   * ganha. Com poucas bikes a faixa é aproximada, e tudo bem — `priceRange` é
   * orientativo no schema.org, não um preço anunciado.
   */
  const faixaDePreco = useMemo(() => {
    const precos = [...(novas.data?.itens ?? []), ...(usadas.data?.itens ?? [])].map(
      (p) => p.precoCentavos,
    )
    if (precos.length === 0) return null
    const menor = Math.min(...precos)
    const maior = Math.max(...precos)
    return menor === maior
      ? formatarPreco(menor)
      : `${formatarPreco(menor)} - ${formatarPreco(maior)}`
  }, [novas.data, usadas.data])

  return (
    <>
      {/*
        `imagem` é obrigatória aqui: o <Meta> remove as meta ESTÁTICAS do
        index.html ao montar (senão haveria canonical duplicado), e sem repor a
        og:image a home ficava sem ela depois do JS. Crawler sem JS continua
        vendo a estática; quem renderiza, como o Googlebot, via nenhuma.
      */}
      <Meta
        titulo={config.seoTitulo}
        descricao={config.seoDescricao}
        caminhoCanonico="/"
        imagem={`${window.location.origin}/compartilhar.jpg`}
      />
      {/*
        LocalBusiness é o que mais rende para loja de bairro: alimenta o painel
        lateral do Google e cruza com o perfil da empresa no Maps.
      */}
      <JsonLd
        dados={{
          '@context': 'https://schema.org',
          '@type': 'BikeStore',
          name: 'Bicicletas Peniel',
          description: config.sobreTexto,
          url: window.location.origin,
          telephone: `+${config.whatsappNumero}`,
          address: { '@type': 'PostalAddress', streetAddress: config.lojaEndereco, addressLocality: 'Goiânia', addressRegion: 'GO', addressCountry: 'BR' },
          ...(config.instagramUrl || config.facebookUrl
            ? { sameAs: [config.instagramUrl, config.facebookUrl].filter(Boolean) }
            : {}),
          ...(config.lojaMapsUrl ? { hasMap: config.lojaMapsUrl } : {}),

          // A imagem alimenta o painel lateral do Google; sem ela o resultado
          // local sai só com texto.
          image: `${window.location.origin}/compartilhar.jpg`,

          /*
           * `openingHours` no formato do schema.org (dia abreviado em inglês e
           * hora 24h), derivado do que o dono escreve no painel.
           *
           * O horário JÁ existia em `config.lojaHorario` e só aparecia no
           * rodapé — era dado pronto que o Google não recebia.
           */
          ...(horarioSchema.length > 0 ? { openingHours: horarioSchema } : {}),

          // Faixa de preço: o Google mostra no resultado local e ajuda a
          // filtrar quem busca por orçamento.
          ...(faixaDePreco ? { priceRange: faixaDePreco } : {}),

          currenciesAccepted: 'BRL',
        }}
      />

      <Hero />
      <TiraDeDiferenciais />

      <SecaoProdutos
        id="novas"
        titulo="Bicicletas Novas"
        subtitulo="Modelos zero km das melhores marcas. Escolha a sua e entre em contato para garantir."
        variante="nova"
        produtos={novas.data?.itens}
        carregando={novas.isPending}
        erro={novas.error}
        aoTentarNovamente={() => void novas.refetch()}
        total={novas.data?.paginacao.total}
        linkVerTodas="/bicicletas?condicao=NOVA"
        fundo="cinza"
      />

      <SecaoProdutos
        id="usadas"
        titulo="Bicicletas Usadas (Revisadas)"
        subtitulo="Oportunidades únicas com custo-benefício excelente. 100% revisadas pela nossa oficina."
        variante="usada"
        produtos={usadas.data?.itens}
        carregando={usadas.isPending}
        erro={usadas.error}
        aoTentarNovamente={() => void usadas.refetch()}
        total={usadas.data?.paginacao.total}
        linkVerTodas="/bicicletas?condicao=USADA"
        fundo="branco"
      />

      <BannerTroca />
    </>
  )
}
